import {computed, ComputedRef, reactive, Ref, ref, UnwrapNestedRefs, watch} from "vue";
import {LocationQueryRaw, RouteLocationNormalizedLoadedGeneric, useRoute} from "vue-router";
import {debounce} from "lodash";
import {PaginationParams} from "@YenzaCT/sdk";

export interface PaginationConfig {
  initialColumns: ColumnsConfig;
  initialSort?: SortOption[];
  initialItemsPerPage?: number;
}

export interface ColumnConfig {
  title: string;
  modelKey: string;
  visible: boolean;
  filter: string | string [];
  filterText?: string;
}

export interface ColumnsConfig {
  [key: string]: ColumnConfig;
}

export type Filters = Record<string, unknown>;

export interface SortOption {
  key: string;
  order: "asc" | "desc";
}

export interface Header {
  [key: string]: string;

  title: string;
  key: string;
}

interface PaginationItem {
  type: "button" | "text";
  value: number | string;
}

export interface FooterData {
  firstPage: number;
  lastPage: number;
  nextPage: number | null;
  prevPage: number | null;
  currentPage: number;
  totalPages: number | null;
  totalDocs: number | null;
  limit: number;
  pageText: string;
  pageSpread: PaginationItem[] | [];
}

interface PaginationData {
  page: number;
  limit: number;
  totalPages: number;
  totalDocs: number | null;
  nextPage: number | null;
  prevPage: number | null;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

function computeHeaders(columns: ColumnsConfig): Header[] {
  return Object.keys(columns)
    .filter((key) => columns[key].visible)
    .map((key): Header => ({
      title: columns[key].title,
      key: columns[key].modelKey
    }));
}

export function usePagination(fetchData: () => Promise<void>, config: PaginationConfig) {
  const {
    initialColumns,
    initialSort = [{
      key: "createdAt",
      order: "asc"
    }],
    initialItemsPerPage = 20
  } = config;

  const columns: UnwrapNestedRefs<ColumnsConfig> = reactive(initialColumns);
  const route = useRoute();

  const headers: ComputedRef<Header[]> = computed(() => computeHeaders(columns));

  // Common filters
  const createdAtDateFilter = ref<Date[]>([]);

  const selectedTenant = ref<string | null>();
  const selectedInstitution = ref<string | null>();
  const selectedCohort = ref<string | null>();

  // Debounced fetch function to avoid multiple rapid API calls
  const debouncedFetchData = debounce(async () => {
    await fetchData();
  }, 300);

  // Single watcher for all column changes
  watch(columns, () => {
    debouncedFetchData();
  }, { deep: true });

  function watchDateFilter(filter: Ref<Date[]>, column: ColumnConfig) {
    watch(() => filter.value, (dates: Date[] | undefined) => {
      const parsedDates = parseDates(dates);
      updateDateRangeFilter(column, parsedDates);
    });
  }

  watch(() => selectedTenant.value, (newTenant: string | undefined) => {
    if (!columns.tenant)
      return;

    columns.tenant.filter = "";
    columns.tenant.filterText = "";

    if (newTenant) {
      columns.tenant.filter = newTenant;
    }
  });

  watch(() => selectedInstitution.value, (newInstitution: string | undefined) => {
    if (!columns.institution)
      return;

    columns.institution.filter = "";
    columns.institution.filterText = "";

    if (newInstitution) {
      columns.institution.filter = newInstitution;
    }
  });

  watch(() => selectedCohort.value, (newCohort: string | undefined) => {
    if (!columns.cohort)
      return;

    columns.cohort.filter = "";
    columns.cohort.filterText = "";

    if (newCohort) {
      columns.cohort.filter = newCohort;
    }
  });

  // Combined watcher for hierarchy filters with debouncing
  // watch(
  //   () => ({
  //     tenant: selectedTenant.value,
  //     institution: selectedInstitution.value,
  //     cohort: selectedCohort.value
  //   }),
  //   (newValues, oldValues) => {
  //     // Update column filters
  //     if (newValues.tenant !== oldValues?.tenant) {
  //       updateTenantInstitutionCohortColumnFilter(columns.tenant, newValues.tenant);
  //     }
  //     if (newValues.institution !== oldValues?.institution) {
  //       updateTenantInstitutionCohortColumnFilter(columns.institution, newValues.institution);
  //     }
  //     if (newValues.cohort !== oldValues?.cohort) {
  //       updateTenantInstitutionCohortColumnFilter(columns.cohort, newValues.cohort);
  //     }
  //   },
  //   { deep: true }
  // );

  /**
   * The current page.
   */
  const page = ref(
    route.query.page ? parseInt(route.query.page as string) : 1
  );

  /**
   * The number of items per page.
   */
  const perPage = ref(route.query.itemsPerPage ? parseInt(route.query.itemsPerPage as string) : initialItemsPerPage);

  const sort: Ref<SortOption[]> = ref(initialSort);
  const searchText = ref("");

  /**
   * Returns how many filters are active
   */
  const activeFilterCount = computed(() => {
    let count = 0;

    for (const key in columns) {
      const column = columns[key];

      if (Array.isArray(column.filter) && column.filter.length > 0) {
        count++;
      }

      if (column.filter && typeof column.filter === "string") {
        count++;
      }
    }

    return count;
  });

  /**
   * Returns the current filters.
   */
  function resetFilter(ignoredFields: string[] = []) {
    for (const key in columns) {
      if (ignoredFields.includes(key)) continue;
      const column = columns[key];
      column.filter = "";
      column.filterText = "";

      if (key === "tenant") selectedTenant.value = null;
      if (key === "institution") selectedInstitution.value = null;
      if (key === "cohort") selectedCohort.value = null;
    }

    // TODO: Account for ignored fields here....
  }

  /**
   * Sets the current filters to be used in a URL.
   * @param filters The current filters.
   */
  function createUrlFilterParameters(filters: PaginationParams): LocationQueryRaw {
    const newQueryParams: LocationQueryRaw = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        newQueryParams[key] = value.toString();
      }
    });

    return newQueryParams;
  }

  /**
   * Parses the given dates and adds one day to the end date.
   * @param dates The dates to parse.
   */
  function parseDates(dates: Date[] | undefined) {
    return dates?.map((date) => new Date(date)) || [];
  }

  /**
   * Updates the date range filter.
   * @param column The column to update.
   * @param dates The dates to filter by.
   */
  function updateDateRangeFilter(column: ColumnConfig, dates: Date[] | undefined) {
    column.filter = [];
    column.filterText = "";
    if (dates) {
      const [filterStart, filterEnd] = dates;

      column.filter.push(filterStart.toISOString());
      column.filterText = filterStart.toDateString();

      column.filter.push(filterEnd.toISOString());
      column.filterText += ` to ${filterEnd.toDateString()}`;
    }
  }

  /**
   * Event handler for updating the number of items per page.
   * @param numPerPage The number of items per page to show.
   */
  async function onUpdatePerPage(numPerPage: number) {
    perPage.value = numPerPage;
    await fetchData();
  }

  /**
   * Event handler for sorting the table.
   * @param sortOptions
   */
  async function onSort(sortOptions: SortOption[]) {
    if (sortOptions.length === 0) {
      // v-data-table passes empty array when sorting desc.
      sort.value = [{
        key: sort.value[0].key,
        order: sort.value[0].order === "asc" ? "desc" : "asc"
      }];
    } else {
      sort.value = sortOptions;
    }
    await fetchData();
  }

  /**
   * Event handler for going to the next page.
   */
  const onNext = async () => flipPage(1);

  /**
   * Event handler for going to the previous page.
   */
  const onPrev = async () => flipPage(-1);

  /**
   * Flips the page by the given number.
   * @param num
   */
  const flipPage = async (num: number) => {
    page.value = page.value + num;
    await fetchData();
  };

  const onUpdateSearch = (text: string) => debounce(async () => {
    searchText.value = text;
    await fetchData();
  }, 1000);

  /**
   * Handles changing the page when selecting a page number in the pagination footer.
   * @param pageNumber
   */
  async function onPageChange(pageNumber: number | string) {
    page.value = typeof pageNumber === "string" ? parseInt(pageNumber) : pageNumber;
    await fetchData();
  }

  function setupBasicParams(
    page: Ref<number, number>,
    perPage: Ref<number, number>,
    sort: Ref<SortOption[], SortOption[]>,
    route: RouteLocationNormalizedLoadedGeneric
  ) {
    page.value = route.query.page ? parseInt(route.query.page as string) : page.value;
    perPage.value = route.query.size ? parseInt(route.query.size as string) : perPage.value;
    sort.value[0].key = route.query.sortBy as string || sort.value[0].key;
    sort.value[0].order = route.query.sortOrder as "asc" | "desc" || sort.value[0].order;
  }

  return {
    columns,
    headers,
    page,
    perPage,
    sort,
    activeFilterCount,
    createdAtDateFilter,
    selectedTenant,
    selectedInstitution,
    selectedCohort,
    resetFilter,
    createUrlFilterParameters,
    watchDateFilter,
    onUpdatePerPage,
    onSort,
    onNext,
    onPrev,
    onPageChange,
    onUpdateSearch,
    setupFooterData,
    setupBasicParams
  };
}

/**
   * Setup of dataset for use in pagination footer.
   * @param paginationData
   */
export function setupFooterData(paginationData: PaginationData) {
  const rangeUpperLimit = paginationData.page * paginationData.limit;
  const rangeLowerLimit = rangeUpperLimit - paginationData.limit + 1;

  const footerData: FooterData = {
    firstPage: 1,
    limit: paginationData.limit,
    lastPage: paginationData.totalPages,
    nextPage: paginationData.nextPage,
    prevPage: paginationData.prevPage,
    currentPage: paginationData.page,
    totalPages: paginationData.totalPages || null,
    totalDocs: paginationData.totalDocs || null,
    pageText: "",
    pageSpread: [],
  };

  if (paginationData.hasNextPage || paginationData.hasPrevPage) {
    // TODO: Improve
    const ofTotal = paginationData.totalDocs ? " of " + paginationData.totalDocs : " of many";
    const upperLimit = paginationData.totalDocs && rangeUpperLimit < paginationData.totalDocs
      ? rangeUpperLimit : paginationData.totalDocs ? paginationData.totalDocs : rangeUpperLimit;

    footerData.pageText = "Showing " + rangeLowerLimit + " - " + upperLimit + ofTotal;
  } else {
    footerData.pageText = "Showing " + rangeLowerLimit + " - " + rangeUpperLimit;
  }

  footerData.pageSpread = generatePaginationSpread(paginationData.page, paginationData.totalPages);

  return footerData;
}

/**
  * Creates an array containing the page numbers to be visible, and the ellipses for hidden pages.
  * @param currentPage
  * @param lastPage
  */
function generatePaginationSpread(currentPage: number, lastPage: number): PaginationItem[] {
  const pagination: PaginationItem[] = [];

  if (lastPage === undefined) {
    return [];
  } else {
    // Handle left side of the current page
    if (currentPage - 1 >= 2) {
      pagination.push({type: "button", value: 1});
      pagination.push({type: "text", value: "..."});
      pagination.push({type: "button", value: currentPage - 1});
    } else {
      for (let i = 1; i < currentPage; i++) {
        pagination.push({type: "button", value: i});
      }
    }

    // Add the current page
    pagination.push({type: "button", value: currentPage});

    // Handle right side of the current page
    if (lastPage - currentPage >= 2) {
      pagination.push({type: "button", value: currentPage + 1});
      pagination.push({type: "text", value: "..."});
      pagination.push({type: "button", value: lastPage});
    } else {
      for (let i = currentPage + 1; i <= lastPage; i++) {
        pagination.push({type: "button", value: i});
      }
    }

    return pagination;
  }
}

/**
 * Data factory to create a default FooterData object.
 */
export function footerDataFactory() {
  const footerData: FooterData = {
    firstPage: 1,
    lastPage: 1,
    nextPage: null,
    prevPage: null,
    currentPage: 1,
    totalPages: null,
    totalDocs: null,
    limit: 20,
    pageText: "",
    pageSpread: []
  };
  return footerData;
}
